import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  fetchCategories,
  fetchCategoriesAll,
  fetchCategory,
  fetchCategoryByName,
  createCategory,
  updateCategory,
  deleteCategory,
} from "../api/categoryApi";
import type { CategorySearchOptions, CategoryProduct } from "../api/categoryApi";

export type { Category, CategoryBranch, CategoryProduct, CategorySearchOptions } from "../api/categoryApi";

// Query Keys
export const categoryKeys = {
  all: ["categories"] as const,
  lists: () => [...categoryKeys.all, "list"] as const,
  list: (filters: CategorySearchOptions) => [...categoryKeys.lists(), filters] as const,
  listAll: () => [...categoryKeys.all, "listAll"] as const,
  details: () => [...categoryKeys.all, "detail"] as const,
  detail: (id: string) => [...categoryKeys.details(), id] as const,
};

// Hooks
export const useCategories = (searchOptions: CategorySearchOptions) => {
  return useQuery({
    queryKey: categoryKeys.list(searchOptions),
    queryFn: () => fetchCategories(searchOptions),
  });
};

export const useCategoriesAll = () => {
  return useQuery({
    queryKey: categoryKeys.listAll(),
    queryFn: fetchCategoriesAll,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCategory = (id: string) => {
  return useQuery({
    queryKey: categoryKeys.detail(id),
    queryFn: () => fetchCategory(id),
    enabled: !!id,
  });
};

export const useCreateCategory = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCategory,
    onSuccess: (data) => {
      toast.success("Success!", {
        description: data.message || "Category created successfully",
      });
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message ||
            error.message
          : "Failed to create category";
      toast.error("Something went wrong", {
        description: message,
      });
    },
  });
};

export const useUpdateCategory = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCategory,
    onSuccess: (data) => {
      toast.success("Success!", {
        description: data.message || "Category updated successfully",
      });
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message ||
            error.message
          : "Failed to update category";
      toast.error("Something went wrong", {
        description: message,
      });
    },
  });
};

export const useDeleteCategory = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCategory,
    onSuccess: (data) => {
      toast.success("Success!", {
        description: data.message || "Category deleted successfully",
      });
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message ||
            error.message
          : "Failed to delete category";
      toast.error("Something went wrong", {
        description: message,
      });
    },
  });
};

// Hook to get products for a specific category (uses cached categories data)
export const useCategoryProducts = (categoryName: string, branchName?: string) => {
  return useQuery({
    queryKey: [...categoryKeys.all, "products", categoryName, branchName] as const,
    queryFn: async (): Promise<CategoryProduct[]> => {
      const category = await fetchCategoryByName(categoryName, branchName);
      return category?.products || [];
    },
    enabled: !!categoryName,
    staleTime: 5 * 60 * 1000,
  });
};
